(ns common.schema.entities.card
  (:require [common.schema.schema-utils :as su]
            [clojure.string :as string]
            [common.schema.option-filters :as option-filters]
            [clojure.set :as set]
            [common.schema.entity-type-names :as entity-type-names]))

(defn temp-card?
  [entity]
  (and (= (:entity/type entity) :entity.type/card)
       (string? (:db/id entity))))

(defn real-card?
  [entity]
  (and (= (:entity/type entity) :entity.type/card)
       (int? (:db/id entity))))

(defn select-create-fields
  [card]
  (-> card
      (select-keys
       [:card/type
        :card/entity-type
        :card/title
        :card/config
        :card/description
        :card/access-level])))

(defn select-update-fields
  [card]
  (-> card
      (select-keys
       [:card/title
        :card/description
        :card/config])))

(defn property-list-card->entity-fields
  [card]
  (->> card
       :card/config
       :rows
       (mapv (fn [row]
               (->> row
                    :cells
                    (mapv (fn [cell]
                            (:field cell))))))
       (flatten)
       (set)))

(defn entity-associations-card->associated-entity-types
  [card]
  (->> card
       :card/config
       :tables
       (mapv
        (fn [table]
          (let [entity-type (:entity-type table)
                fields (->> table
                            :columns
                            (filter (fn [col]
                                      (= (:source col)
                                         :associated-entity)))
                            (mapv (fn [col]
                                    (:field col)))
                            (set)
                            (vec))]
            {:entity-type entity-type
             :fields fields})))))

(defn private-card?
  [card]
  (= (:card/access-level card) :card.access-level/private))

(defn restricted-card?
  [card]
  (= (:card/access-level card) :card.access-level/restricted))

(defn all-users-card?
  [card]
  (= (:card/access-level card) :card.access-level/all-users))

(defn card-owner?
  [account card]
  (let [card-owner (:common/owner card)
        card-owner-id (or (:db/id card-owner)
                          card-owner)
        account-id (or (:db/id account) account)]
    (= account-id card-owner-id)))

(defn has-share-access?
  [account _ card]
  (card-owner? account card))

(defn has-write-access?
  [account account-current-permissions card]
  (let [account-id (or (:db/id account) account)
        account-teams (-> account-current-permissions
                          :account-team-memberships
                          (set))
        write-access-ids (->> card
                              :card/write-access
                              (mapv (fn [entity-or-id]
                                      (or (:db/id entity-or-id)
                                          entity-or-id))))]
    (cond
      (private-card? card)
      (card-owner? account card)

      (restricted-card? card)
      (or (card-owner? account card)
          (some (set/union #{account-id} account-teams) write-access-ids))
      ;; FIXME: handle this once card access levels are implemented
      (all-users-card? card)
      (card-owner? account card))))

(defn has-read-access?
  [account account-current-permissions card]
  (let [account-id (or (:db/id account) account)
        account-teams (-> account-current-permissions
                          :account-team-memberships
                          (set))
        read-access-ids (->> card
                             :card/read-access
                             (mapv (fn [entity-or-id]
                                     (or (:db/id entity-or-id)
                                         entity-or-id))))]
    (cond
      ;; Private card be however read if the card is contained in a view that the user has access to. Handled elsewhere
      (private-card? card)
      (card-owner? account card)

      (restricted-card? card)
      (or (card-owner? account card)
          (some (set/union #{account-id} account-teams) read-access-ids)
          (has-write-access? account account-current-permissions card)
          (has-share-access? account account-current-permissions card))

      (all-users-card? card)
      true)))
;; FIXME: allow updating access later
(defn select-updatable-fields
  [card]
  (-> card
      (select-keys
       [:card/title
        :card/description
        :card/config
        #_:card/access-level
        #_:card/read-access
        #_:card/write-access
        #_:card/share-access])))

(defn select-permission-fields
  [card]
  (-> card
      (select-keys
       [:card/access-level
        :card/read-access
        :card/write-access
        :card/share-access])))

(def card-type-options
  [{:value :card.type/property-list
    :label "Property list"}
   {:value :card.type/activities
    :label "Activities"}
   {:value :card.type/associations
    :label "Associations"}
   {:value :card.type/attachments
    :label "Attachments"}])

(defn get-card-type-description
  [{:keys [card-type entity-type tr]}]
  (case card-type
    :card.type/property-list
    (tr :card/property-list-description [(string/lower-case (tr entity-type))])

    :card.type/activities
    (tr :card/activities-description [(string/lower-case (tr entity-type))])

    :card.type/associations
    (tr :card/associations-description [(string/lower-case (entity-type-names/get-plural entity-type :en))])

    :card.type/attachments
    (tr :card/attachments-description [(string/lower-case (tr entity-type))])))

(def access-level-options
  [{:value   :card.access-level/private
    :label "Private"}
   {:value   :card.access-level/restricted
    :label "Restricted"}
   {:value   :card.access-level/all-users
    :label "All users"}])

(def card-entity-type-options
  (->> [:entity.type/oppo
        :entity.type/sales-opportunity
        :entity.type/exit-opportunity
        :entity.type/fundraising-opportunity
        :entity.type/person
        :entity.type/company
        :entity.type/task
        :entity.type/investor
        :entity.type/real-estate
        :entity.type/fund
        :entity.type/asset
        :entity.type/fof-asset
        :entity.type/other-asset
        :entity.type/holding
        :entity.type/calculated-indirect-holding
        :entity.type/fund-commitment
        :entity.type/kyc-profile
        :entity.type/screening-profile]
       (mapv (fn [entity-type]
               {:key   entity-type
                ;; TODO: cannot use loc/tr because of circular dependency, maybe do not need these options in UI
                :label ""}))))

;; TODO: add more cases and split into smaller parts
(defn default-card
  [{:keys [temp-id card-type entity-type account-id tr]}]
  (cond->
   {:db/id temp-id
    :entity/type :entity.type/card
    :card/type card-type
    :card/config {}
    :card/entity-type entity-type
    :card/access-level :card.access-level/private
    :common/owner account-id}

    (= card-type :card.type/activities)
    (merge
     {:card/title (tr :card.type/activities)})

    (= card-type :card.type/attachments)
    (merge
     {:card/title (tr :card.type/attachments)
      :card/description (tr :card/attachments-description [(-> entity-type
                                                               tr
                                                               string/lower-case)])})

    (= card-type :card.type/tasks)
    (merge
     {:card/title (tr :card.type/tasks)
      :card/description (tr :card/tasks-description [(-> entity-type
                                                         tr
                                                         string/lower-case)])
      :card/config
      {:table {:columns    [{:field :task/title}
                            {:field :task/due}
                            {:field :task/assignee}
                            {:field :task/completed?}]
               :sort-model [{:field :task/completed?
                             :direction :asc}]
               :entity-type :entity.type/task}
       :entity-peek {:entity-type :entity.type/task
                     :visible-fields-strategy :fields-that-have-value
                     :visible-fields [{:field :task/title}]}}})

    (= card-type :card.type/fees)
    (merge
     {:card/title (tr :card.type/fees)
      :card/description (tr :card/fees-description [(-> entity-type tr string/lower-case)])
      :card/config
      {:table {:columns    [{:field :fee/payer}
                            {:field :fee/cur-amount}
                            {:field :fee/status}
                            {:field :fee/date-fee-date}
                            {:field :fee/type}]
               :sort-model [{:field :fee/cur-amount
                             :direction :desc}]
               :entity-type :entity.type/fee}
       :entity-peek {:entity-type :entity.type/fee
                     :visible-fields-strategy :fields-that-have-value
                     :visible-fields [{:field :fee/payer}]}}})))

(def schema
  (vec
   (concat
    (su/text-simple-field :card/title
                          "Title"
                          {:name-attribute           :card/title
                           :lower-case-str?          true
                           :not-clearable?           true
                           :mandatory-config         :mandatory/always
                           :system-required?         true
                           :system-semantics
                           su/user-provided-system-required-field-semantics
                           :editor-type              :editor.type/text-simple
                           :default-field-visibility :in-use
                           :authorization su/access-user-user})

    (su/text-multiline-field :card/description
                             "Description"
                             {:authorization su/access-user-user})

    (su/text-edn-field :card/config
                       "Card config"
                       {:authorization su/access-user-user})

    (su/keyword-field :card/type
                      "Card type"
                      card-type-options
                      {:default-field-visibility :in-use})

    (su/keyword-field :card/entity-type
                      "Card entity type"
                      card-entity-type-options
                      {:authorization su/access-user-user})

    ;; TODO: should this be :card/owner or :common/owner?
    (su/single-ref-field :card/owner
                         "Card owner"
                         [:entity.type/account]
                         {:authorization      su/access-user-user
                          :option-filter-fn   option-filters/active-users
                          :os-filter-terms-fn (fn [{:keys [] :as args}]
                                                {"extras.active-user" true})})

    (su/keyword-field :card/access-level
                      "Access level"
                      access-level-options
                      {:authorization su/access-user-user})

    (su/multi-ref-field :card/read-access
                        "Read access"
                        [:entity.type/account :entity.type/team]
                        {:default-field-visibility :in-use})

    (su/multi-ref-field :card/write-access
                        "Write access"
                        [:entity.type/account :entity.type/team]
                        {:default-field-visibility :in-use})

    (su/multi-ref-field :card/share-access
                        "Share access"
                        [:entity.type/account :entity.type/team]
                        {:default-field-visibility :in-use}))))
